import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '@/App';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Card } from '@/components/ui/card';
import Layout from '@/components/Layout';
import { DashboardSkeleton } from '@/components/SkeletonLoaders';
import { 
  Eye, 
  MessageSquare, 
  Star, 
  TrendingUp, 
  Users, 
  Calendar,
  ArrowUp,
  ArrowDown,
  Building2,
  Percent,
  Briefcase,
  Heart,
  BadgeCheck
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// StatCard component
const StatCard = ({ title, value, subtitle, icon: Icon, color, trend }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100"
  >
    <div className="flex items-start justify-between">
      <div>
        <p className="text-sm text-slate-500 mb-1">{title}</p>
        <p className="text-3xl font-bold text-[#344B71]">{value}</p>
        {subtitle && (
          <p className="text-sm text-slate-400 mt-1">{subtitle}</p>
        )}
      </div>
      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${color}`}>
        <Icon className="w-6 h-6 text-white" />
      </div>
    </div>
    {trend !== undefined && (
      <div className="flex items-center gap-1 mt-3 text-sm">
        {trend === 'up' ? (
          <>
            <ArrowUp className="w-4 h-4 text-green-500" />
            <span className="text-green-500">In crescita</span>
          </>
        ) : trend === 'down' ? (
          <>
            <ArrowDown className="w-4 h-4 text-red-500" />
            <span className="text-red-500">In calo</span>
          </>
        ) : (
          <span className="text-slate-400">Stabile</span>
        )}
        <span className="text-slate-400 ml-1">vs settimana scorsa</span>
      </div>
    )}
  </motion.div>
);

// Rating Distribution Bar
const RatingBar = ({ stars, count, total }) => {
  const percentage = total > 0 ? (count / total) * 100 : 0;
  return (
    <div className="flex items-center gap-3">
      <span className="text-sm text-slate-500 w-6">{stars}★</span>
      <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
        <div 
          className="h-full bg-[#E8A007] rounded-full transition-all duration-500"
          style={{ width: `${percentage}%` }}
        />
      </div>
      <span className="text-sm text-slate-600 w-8 text-right">{count}</span>
    </div>
  );
};

const AnalyticsDashboard = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.user_type !== 'professional') {
      navigate('/dashboard');
      return;
    }
    fetchAnalytics();
  }, [user, navigate]);

  const fetchAnalytics = async () => {
    try {
      const res = await axios.get(`${API}/analytics/professional/advanced`);
      setAnalytics(res.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-6 py-8 mt-20">
          <DashboardSkeleton />
        </div>
      </Layout>
    );
  }

  if (!analytics) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-6 py-8 mt-20">
          <Card className="p-8 text-center">
            <p className="text-slate-600">Impossibile caricare le analytics</p>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-6 py-8 mt-20">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="text-3xl font-playfair font-bold text-[#344B71] mb-2">
            Analytics Dashboard
          </h1>
          <p className="text-slate-600">
            Monitora le performance del tuo profilo professionale
          </p>
        </motion.div>

        {/* Main Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatCard
            title="Visualizzazioni Profilo"
            value={analytics.profile?.views || 0}
            icon={Eye}
            color="bg-[#344B71]"
          />
          <StatCard
            title="Messaggi Ricevuti"
            value={analytics.messages?.received || 0}
            subtitle={`${analytics.messages?.last_7_days || 0} ultimi 7 giorni`}
            icon={MessageSquare}
            color="bg-[#E8A007]"
            trend={analytics.trends?.messages_trend}
          />
          <StatCard
            title="Valutazione Media"
            value={analytics.reviews?.average_rating > 0 ? analytics.reviews.average_rating.toFixed(1) : '-'}
            subtitle={`${analytics.reviews?.total || 0} recensioni`}
            icon={Star}
            color="bg-green-500"
          />
          <StatCard
            title="Tasso Conversione"
            value={`${analytics.performance?.conversion_rate || 0}%`}
            subtitle="da visite a conversazioni"
            icon={Percent}
            color="bg-purple-500"
          />
        </div>

        {/* Secondary Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="p-4 bg-white">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#344B71]/10 flex items-center justify-center">
                <Building2 className="w-5 h-5 text-[#344B71]" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#344B71]">{analytics.messages?.companies_contacted || 0}</p>
                <p className="text-xs text-slate-500">Aziende contattate</p>
              </div>
            </div>
          </Card>
          <Card className="p-4 bg-white">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#E8A007]/10 flex items-center justify-center">
                <Users className="w-5 h-5 text-[#E8A007]" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#344B71]">{analytics.messages?.total_conversations || 0}</p>
                <p className="text-xs text-slate-500">Conversazioni totali</p>
              </div>
            </div>
          </Card>
          <Card className="p-4 bg-white">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center">
                <Briefcase className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#344B71]">{analytics.performance?.listings_created || 0}</p>
                <p className="text-xs text-slate-500">Annunci creati</p>
              </div>
            </div>
          </Card>
          <Card className="p-4 bg-white">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center">
                <Heart className="w-5 h-5 text-red-500" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#344B71]">{analytics.performance?.favorites_received || 0}</p>
                <p className="text-xs text-slate-500">Preferiti ricevuti</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Detailed Sections */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Messages Details */}
          <Card className="p-6 bg-white">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-[#E8A007]/10 flex items-center justify-center">
                <MessageSquare className="w-5 h-5 text-[#E8A007]" />
              </div>
              <h3 className="text-lg font-semibold text-[#344B71]">Attività Messaggi</h3>
            </div>
            
            <div className="space-y-4">
              <div className="flex justify-between items-center p-3 bg-slate-50 rounded-xl">
                <div className="flex items-center gap-3">
                  <MessageSquare className="w-5 h-5 text-slate-400" />
                  <span className="text-slate-600">Messaggi ricevuti</span>
                </div>
                <span className="font-semibold text-[#344B71]">{analytics.messages?.received || 0}</span>
              </div>
              
              <div className="flex justify-between items-center p-3 bg-slate-50 rounded-xl">
                <div className="flex items-center gap-3">
                  <MessageSquare className="w-5 h-5 text-slate-400" />
                  <span className="text-slate-600">Messaggi inviati</span>
                </div>
                <span className="font-semibold text-[#344B71]">{analytics.messages?.sent || 0}</span>
              </div>
              
              <div className="flex justify-between items-center p-3 bg-slate-50 rounded-xl">
                <div className="flex items-center gap-3">
                  <Calendar className="w-5 h-5 text-slate-400" />
                  <span className="text-slate-600">Ultimi 7 giorni</span>
                </div>
                <span className="font-semibold text-[#344B71]">{analytics.messages?.last_7_days || 0}</span>
              </div>
              
              <div className="flex justify-between items-center p-3 bg-slate-50 rounded-xl">
                <div className="flex items-center gap-3">
                  <Calendar className="w-5 h-5 text-slate-400" />
                  <span className="text-slate-600">Ultimi 30 giorni</span>
                </div>
                <span className="font-semibold text-[#344B71]">{analytics.messages?.last_30_days || 0}</span>
              </div>
              
              <div className="flex justify-between items-center p-3 bg-[#E8A007]/10 rounded-xl">
                <div className="flex items-center gap-3">
                  <TrendingUp className="w-5 h-5 text-[#E8A007]" />
                  <span className="text-slate-700 font-medium">Tasso di risposta</span>
                </div>
                <span className="font-bold text-[#E8A007]">{analytics.performance?.response_rate || 0}%</span>
              </div>
            </div>
          </Card>

          {/* Reviews & Rating Distribution */}
          <Card className="p-6 bg-white">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center">
                <Star className="w-5 h-5 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-[#344B71]">Recensioni e Valutazioni</h3>
            </div>
            
            {analytics.reviews?.total > 0 ? (
              <>
                {/* Rating Summary */}
                <div className="flex items-center gap-4 mb-6 p-4 bg-slate-50 rounded-xl">
                  <div className="text-center">
                    <p className="text-4xl font-bold text-[#344B71]">{analytics.reviews.average_rating.toFixed(1)}</p>
                    <div className="flex items-center justify-center gap-1 mt-1">
                      {[1, 2, 3, 4, 5].map(star => (
                        <Star 
                          key={star}
                          className={`w-4 h-4 ${star <= Math.round(analytics.reviews.average_rating) ? 'fill-[#E8A007] text-[#E8A007]' : 'text-slate-300'}`}
                        />
                      ))}
                    </div>
                    <p className="text-xs text-slate-500 mt-1">{analytics.reviews.total} recensioni</p>
                  </div>
                  <div className="flex-1 space-y-2">
                    {[5, 4, 3, 2, 1].map(stars => (
                      <RatingBar 
                        key={stars}
                        stars={stars}
                        count={analytics.reviews.rating_distribution?.[stars] || 0}
                        total={analytics.reviews.total}
                      />
                    ))}
                  </div>
                </div>

                {/* Collaboration Types */}
                {analytics.reviews.collaboration_types && Object.keys(analytics.reviews.collaboration_types).length > 0 && (
                  <div>
                    <p className="text-sm text-slate-500 mb-3">Tipi di collaborazione</p>
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(analytics.reviews.collaboration_types).map(([type, count]) => (
                        <span 
                          key={type}
                          className="px-3 py-1 bg-[#344B71]/10 text-[#344B71] rounded-full text-sm"
                        >
                          {type === 'consulting' ? 'Consulenza' :
                           type === 'project' ? 'Progetto' :
                           type === 'audit' ? 'Audit' :
                           type === 'training' ? 'Formazione' : type} ({count})
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-8 text-slate-500">
                <Star className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>Nessuna recensione ancora</p>
                <p className="text-sm mt-1">Le recensioni appariranno qui quando le aziende valuteranno il tuo lavoro</p>
              </div>
            )}
          </Card>
        </div>

        {/* Profile Status */}
        <Card className="mt-6 p-6 bg-white">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
              <BadgeCheck className="w-5 h-5 text-purple-600" />
            </div>
            <h3 className="text-lg font-semibold text-[#344B71]">Stato Profilo</h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className={`p-4 rounded-xl ${analytics.profile?.is_verified ? 'bg-green-50 border border-green-200' : 'bg-slate-50'}`}>
              <div className="flex items-center gap-2">
                <BadgeCheck className={`w-5 h-5 ${analytics.profile?.is_verified ? 'text-green-600' : 'text-slate-400'}`} />
                <span className={`font-medium ${analytics.profile?.is_verified ? 'text-green-700' : 'text-slate-600'}`}>
                  {analytics.profile?.is_verified ? 'Profilo Verificato' : 'Non Verificato'}
                </span>
              </div>
            </div>
            
            <div className={`p-4 rounded-xl ${analytics.profile?.iscrizione_albo ? 'bg-blue-50 border border-blue-200' : 'bg-slate-50'}`}>
              <div className="flex items-center gap-2">
                <Briefcase className={`w-5 h-5 ${analytics.profile?.iscrizione_albo ? 'text-blue-600' : 'text-slate-400'}`} />
                <span className={`font-medium ${analytics.profile?.iscrizione_albo ? 'text-blue-700' : 'text-slate-600'}`}>
                  {analytics.profile?.iscrizione_albo ? 'Iscritto all\'Albo' : 'Non iscritto all\'Albo'}
                </span>
              </div>
            </div>
            
            <div className="p-4 rounded-xl bg-[#344B71]/5 border border-[#344B71]/20">
              <div className="flex items-center gap-2">
                <Eye className="w-5 h-5 text-[#344B71]" />
                <span className="font-medium text-[#344B71]">
                  {analytics.profile?.views || 0} visualizzazioni totali
                </span>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </Layout>
  );
};

export default AnalyticsDashboard;
